import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { ModernizedStackedBarChartData } from 'src/app/models/chart-models/modernized-stacked-bar-chart';
import { PortfolioHealthCheckService } from '../../../services/portfolio-health-check/portfolio-health-check.service';
import { PhcData, PhcUrl } from '../../../models/interfaces/portfolio-health-check-data';
import { camelToKebabCase, getPHCUrls } from '../../../utils/functions.utils';
import { PageHelpService } from 'src/app/components/page-help/page-help.component.service';
import {
  AdobeAnalyticsService,
  CallToActionType,
} from 'src/app/services/adobe-analytics/adobe-analytics.service';
import { AssetMixViewChoice } from 'src/app/components/portfolio-health-check/factor-card/factor-card.interface';

type Accordions = 'use-available-cash-accordion' | 'sell-accordion' | 'contribute-accordion';

const accordionsIsOpenStatusForTagging: Record<Accordions, boolean> = {
  'use-available-cash-accordion': false,
  'sell-accordion': false,
  'contribute-accordion': false,
};

@Component({
  selector: 'PW1-rebalancing-your-portfolio',
  templateUrl: './rebalancing-your-portfolio.component.html',
  styleUrls: ['./rebalancing-your-portfolio.component.scss'],
})
export class RebalancingYourPortfolioComponent implements AfterViewInit, OnDestroy {

  private resizeObserver: ResizeObserver | undefined;
  private assetMixViewSelected: AssetMixViewChoice = 'percentage';

  actualChartDataAssetMix: ModernizedStackedBarChartData;
  targetChartDataAssetMix: ModernizedStackedBarChartData;
  chartDataGlobalStock: ModernizedStackedBarChartData;

  data: PhcData;

  assetMixAboveTarget: string = '';
  assetMixByReducing: string = '';
  assetMixBelowTarget: string = '';
  assetMixAddingMore: string = '';
  globalStockOverUnder: string = '';
  globalStockAddingMore: string = '';

  urls: PhcUrl = {};

  wordsToSentenceReducer = (prev, curr, ind, arr) => {
    if (!prev) {
      return curr;
    }
    if (ind === arr.length - 1) {
      prev += ` and ${curr}`;
      return prev;
    }
    prev += `, ${curr}`;
    return prev;
  };

  constructor(
    private pw3Service: PortfolioHealthCheckService,
    public readonly pageHelpService: PageHelpService,
    private adobeAnalyticsService: AdobeAnalyticsService,
  ) {
    this.pw3Service.getDataPayload().subscribe((data) => {
      if (this.pw3Service.isLoading.value) return;
      this.data = data;
      this.updateChartDataByAssetMixView();
    });

    this.urls = getPHCUrls();
  }

  updateChartDataByAssetMixView() {
    if (!this.data) return;

    const {
      portfolioFactors: { assetMix, domesticStock },
    } = this.data;

    this.actualChartDataAssetMix = {
      chartId: 'actualChartDataAssetMix',
      chartLabel: 'Current',
      chartSlices: [
        {
          color: '#1ca29c',
          label: 'Stocks',
          name: 'stocks',
          value: assetMix[this.assetMixViewSelected].current.stocks,
        },
        {
          color: '#bf8200',
          label: 'Bonds',
          name: 'bonds',
          value: assetMix[this.assetMixViewSelected].current.bonds,
        },
        {
          color: '#717777',
          label: 'Cash',
          name: 'cash',
          value: assetMix[this.assetMixViewSelected].current.cash,
        },
      ],
      usesMasks: false,
    };
    this.targetChartDataAssetMix = {
      chartId: 'targetChartDataAssetMix',
      chartLabel: 'Target',
      chartSlices: [
        {
          color: '#1ca29c',
          label: 'Stocks',
          name: 'stocks',
          value: assetMix[this.assetMixViewSelected].target.stocks,
        },
        {
          color: '#bf8200',
          label: 'Bonds',
          name: 'bonds',
          value: assetMix[this.assetMixViewSelected].target.bonds,
        },
        {
          color: '#717777',
          label: 'Cash',
          name: 'cash',
          value: assetMix[this.assetMixViewSelected].target.cash,
        },
      ],
      usesMasks: true,
    };

    const assetMixAboveTarget = [];
    const assetMixBelowTarget = [];
    const assetMixAddingMore = [];
    const assetMixByReducing = [];

    if (assetMix.percentage.current.stocks > assetMix.percentage.target.stocks && assetMix.percentage.target.stocks !== 0) {
      assetMixAboveTarget.push('stocks');
      assetMixByReducing.push('stocks');
      assetMixAddingMore.push('bonds');
    }
    if (assetMix.percentage.current.bonds > assetMix.percentage.target.bonds && assetMix.percentage.target.bonds !== 0) {
      assetMixAboveTarget.push('bonds');
      assetMixByReducing.push('bonds');
      assetMixAddingMore.push('stocks');
    }
    if (assetMix.percentage.current.cash > assetMix.percentage.target.cash) {
      if (assetMix.percentage.target.cash !== 0) {
        assetMixAboveTarget.push('cash');
      } else {
        assetMixByReducing.push('cash');
      }
    }

    if (
      assetMix.percentage.current.stocks < assetMix.percentage.target.stocks &&
      !assetMixAddingMore.includes('stocks')
    ) {
      assetMixBelowTarget.push('stocks');
      assetMixAddingMore.push('stocks');
    }
    if (assetMix.percentage.current.bonds < assetMix.percentage.target.bonds && !assetMixAddingMore.includes('bonds')) {
      assetMixBelowTarget.push('bonds');
      assetMixAddingMore.push('bonds');
    }
    if (assetMix.percentage.current.cash < assetMix.percentage.target.cash) {
      assetMixBelowTarget.push('cash');
    }

    this.assetMixAboveTarget = assetMixAboveTarget.reduce(this.wordsToSentenceReducer, '');
    if (this.assetMixAboveTarget) {
      this.assetMixAboveTarget = `more ${this.assetMixAboveTarget}`;
    }
    this.assetMixByReducing = assetMixByReducing.reduce(this.wordsToSentenceReducer, '');
    this.assetMixBelowTarget = assetMixBelowTarget.reduce(this.wordsToSentenceReducer, '');
    if (this.assetMixBelowTarget) {
      this.assetMixBelowTarget = `fewer ${this.assetMixBelowTarget}`;
    }
    this.assetMixAddingMore = Array.from(new Set(assetMixAddingMore)).reduce(
      this.wordsToSentenceReducer,
      '',
    );

    this.chartDataGlobalStock = {
      chartId: 'chartDataGlobalStock',
      chartLabel: null,
      chartSlices: [
        {
          color: '#8DCFCB',
          label: 'International stocks',
          name: 'bonds',
          value: domesticStock.data.international.current,
          range: domesticStock.data.international.range,
        },
        {
          color: '#1A9E97',
          label: 'Domestic (U.S.) stocks',
          name: 'stocks',
          value: domesticStock.data.domestic.current,
          range: [],
        },
      ],
      usesMasks: false,
    };

    if (domesticStock.data.international.current > domesticStock.data.international.range[1]) {
      this.globalStockOverUnder = 'over';
      this.globalStockAddingMore = 'domestic';
    } else if (
      domesticStock.data.international.current < domesticStock.data.international.range[0]
    ) {
      this.globalStockOverUnder = 'under';
      this.globalStockAddingMore = 'international';
    } else {
      this.globalStockAddingMore = 'domestic';
    }
  }

  ngAfterViewInit() {
    const fisrtFactorCardEl = document.getElementById("main__card-area--first-child")

    if (!fisrtFactorCardEl) return;

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const el = document.getElementById("main__card-area--last-child")
        el.style.maxWidth = `${entry.target.clientWidth}px`;
      }
    });
    this.resizeObserver.observe(fisrtFactorCardEl)
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  callToAction(name: string, type: CallToActionType) {
    if (name in accordionsIsOpenStatusForTagging) {
      const accordionName = name as Accordions;
      accordionsIsOpenStatusForTagging[accordionName] = !accordionsIsOpenStatusForTagging[accordionName];
      const state = accordionsIsOpenStatusForTagging[accordionName] ? 'open' : 'closed';
      this.callToAction(`${accordionName}-${state}`, type);
    } else {
      this.adobeAnalyticsService.CreateCTATrigger('rebalancing-your-portfolio', name, type);
    }
  }

  openPageHelpDrawer(type: string) {
    this.pageHelpService.setCurrentDrawerType(type);
    this.pageHelpService.togglePageHelpDrawer();
    this.callToAction(camelToKebabCase(type), 'Button');
  }

  assetMixView($event: AssetMixViewChoice) {
    this.assetMixViewSelected = $event
    this.updateChartDataByAssetMixView()
  }
}